import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from './Modal';

/**
 * ConfirmDialog — accessible confirmation dialog built on Modal.
 *
 * Inherits from Modal/Radix:
 *   - role=dialog + aria-modal
 *   - Focus trap during open
 *   - Focus restore to triggering element on close
 *   - Escape key closes (cancel path)
 *   - Click-outside closes (cancel path)
 *   - Portal mounting via useOverlayPortal
 *   - Z-index tier 7 (--aif-z-modal: 1200)
 *
 * Usage (controlled):
 *   const [open, setOpen] = useState(false);
 *   <ConfirmDialog
 *     open={open}
 *     onOpenChange={setOpen}
 *     title="Notiz löschen?"
 *     description="Diese Aktion kann nicht rückgängig gemacht werden."
 *     destructive
 *     onConfirm={async () => { await deleteNote(); setOpen(false); }}
 *   />
 *
 * Usage (imperative via useConfirmDialog):
 *   const { confirm, ConfirmDialogElement } = useConfirmDialog();
 *   const ok = await confirm({ title: 'Löschen?', destructive: true });
 *   if (ok) { ... }
 *   // Render <ConfirmDialogElement /> once at parent
 */
export default function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  destructive = false,
  onConfirm,
  onCancel,
  busy = false,
}) {
  const { t } = useTranslation();
  const [internalBusy, setInternalBusy] = useState(false);

  const isBusy = busy || internalBusy;

  const resolvedConfirmLabel =
    confirmLabel ??
    (destructive
      ? t('confirm.confirmDestructive', { defaultValue: 'Löschen' })
      : t('confirm.confirmDefault', { defaultValue: 'Bestätigen' }));

  const resolvedCancelLabel =
    cancelLabel ?? t('confirm.cancelDefault', { defaultValue: 'Abbrechen' });

  const handleConfirm = useCallback(async () => {
    if (isBusy) return;
    if (!onConfirm) {
      onOpenChange?.(false);
      return;
    }
    setInternalBusy(true);
    try {
      await onConfirm();
      onOpenChange?.(false);
    } finally {
      setInternalBusy(false);
    }
  }, [isBusy, onConfirm, onOpenChange]);

  const handleCancel = useCallback(() => {
    if (isBusy) return;
    onCancel?.();
    onOpenChange?.(false);
  }, [isBusy, onCancel, onOpenChange]);

  return (
    <Modal.Root
      open={open}
      onOpenChange={(v) => {
        if (!v) handleCancel();
        else onOpenChange?.(true);
      }}
    >
      <Modal.Content
        size="sm"
        data-testid="confirm-dialog"
        data-variant={destructive ? 'destructive' : 'default'}
      >
        <Modal.Title data-testid="confirm-dialog-title">{title}</Modal.Title>
        {description && (
          <Modal.Description data-testid="confirm-dialog-description">
            {description}
          </Modal.Description>
        )}

        <Modal.Footer>
          <button
            type="button"
            onClick={handleCancel}
            disabled={isBusy}
            data-testid="confirm-dialog-cancel"
            className="px-4 py-2.5 rounded-xl text-sm text-[var(--aif-text-secondary)] border border-[var(--aif-border-default)] hover:bg-[var(--aif-bg-secondary)] transition disabled:opacity-40"
          >
            {resolvedCancelLabel}
          </button>

          {destructive ? (
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isBusy}
              data-testid="confirm-dialog-confirm"
              autoFocus
              className="px-4 py-2.5 rounded-xl text-sm text-red-400 border border-red-400/30 hover:bg-red-400/10 transition disabled:opacity-40"
            >
              {isBusy
                ? t('confirm.busy', { defaultValue: 'Bitte warten…' })
                : resolvedConfirmLabel}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isBusy}
              data-testid="confirm-dialog-confirm"
              autoFocus
              className="px-4 py-2.5 rounded-xl text-sm text-white bg-blue-600 hover:bg-blue-700 transition disabled:opacity-40"
            >
              {isBusy
                ? t('confirm.busy', { defaultValue: 'Bitte warten…' })
                : resolvedConfirmLabel}
            </button>
          )}
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
}

// ============================================================
// useConfirmDialog — imperative API for inline call sites
// ============================================================

/**
 * Imperative hook. Returns:
 *   - confirm(opts): Promise<boolean>  — true if confirmed, false if cancelled
 *   - ConfirmDialogElement: JSX to render once in parent
 *
 * Use this when migrating from window.confirm() — it preserves the
 * synchronous-feeling call style:
 *
 *   const { confirm, ConfirmDialogElement } = useConfirmDialog();
 *   const ok = await confirm({ title: '...', destructive: true });
 *   if (!ok) return;
 *   await doDelete();
 *   return (<>{ChildContent}{ConfirmDialogElement}</>);
 */
export function useConfirmDialog() {
  const [state, setState] = useState({
    open: false,
    opts: null,
    resolver: null,
  });

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      setState({
        open: true,
        opts: opts || {},
        resolver: resolve,
      });
    });
  }, []);

  const handleOpenChange = useCallback(
    (next) => {
      if (!next && state.resolver) {
        state.resolver(false);
        setState({ open: false, opts: null, resolver: null });
      }
    },
    [state.resolver],
  );

  const handleConfirm = useCallback(async () => {
    if (state.opts?.onConfirm) {
      await state.opts.onConfirm();
    }
    if (state.resolver) state.resolver(true);
    setState({ open: false, opts: null, resolver: null });
  }, [state.opts, state.resolver]);

  const ConfirmDialogElement = (
    <ConfirmDialog
      open={state.open}
      onOpenChange={handleOpenChange}
      title={state.opts?.title || ''}
      description={state.opts?.description}
      confirmLabel={state.opts?.confirmLabel}
      cancelLabel={state.opts?.cancelLabel}
      destructive={state.opts?.destructive}
      onConfirm={handleConfirm}
    />
  );

  return { confirm, ConfirmDialogElement };
}
